import { FoundAddress } from '@components/Onboarding/TeacherOnboarding/types';

import { parsePointCoordinates } from '@utils/coordinateUtils';

export const extractNumbers = (input: string) => input.replace(/\D/g, '');

/**
 * Checks if the API response exactly matches the user's input for postcode and house number.
 */
export const isExactMatch = (apiResult: any, userPostCode: string, userHouseNumber: string) => {
  const userNumbers = extractNumbers(userHouseNumber);
  const apiNumbers = apiResult.huisnummer?.toString() || '';

  return (
    apiResult.postcode?.toLowerCase() === userPostCode.toLowerCase() &&
    userNumbers === apiNumbers &&
    userNumbers.length > 0
  );
};

/**
 * Fetches and validates an address from the PDOK API.
 * @param postCode - The postal code.
 * @param houseNumber - The house number.
 */
export const fetchAddressFromAPI = async (
  postCode: string,
  houseNumber: string,
): Promise<FoundAddress> => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_PDOK_API}?q=${postCode}+${houseNumber}&fq=type:adres&rows=1`,
  );

  if (!response.ok) {
    throw new Error('API request failed');
  }

  const data = await response.json();
  const apiAddress = data.response.docs[0];

  if (!apiAddress || !apiAddress.huisnummer || !isExactMatch(apiAddress, postCode, houseNumber)) {
    throw new Error('Address not found with this combination');
  }

  const coordinates = parsePointCoordinates(apiAddress.centroide_ll);
  if (!coordinates) {
    throw new Error('Coordinates could not be parsed from API response');
  }

  // If everything is valid, return the structured address object.
  return {
    street: apiAddress.straatnaam,
    city: apiAddress.woonplaatsnaam,
    postCode: apiAddress.postcode,
    houseNumber: apiAddress.huis_nlt,
    fullAddress: apiAddress.weergavenaam,
    coordinates: coordinates,
  };
};
